import type { Automation } from "@repo/app-types/entities/automation";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Switch } from "@repo/ui/Switch/switch";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import type { FC } from "react";
import { AutomationInfoPopover } from "./automationInfoPopover.js";

export interface AutomationDetailActionsProps {
  automation: Automation;
  createdByName?: string;
  /**
   * Renders Delete / Run preview / the enable toggle disabled for viewers who
   * cannot modify automations (reviewers).
   */
  isReadOnly?: boolean;
  onToggleEnabled: (isEnabled: boolean) => void;
  onDeleteRequest?: () => void;
  onRunPreview?: () => void;
  isRunPreviewLoading?: boolean;
  /** Why Run preview is disabled, shown as its tooltip. */
  runPreviewDisabledReason?: string;
}

/**
 * The automation detail page header's actions: the Delete / Run preview / enable
 * controls and the info popover (last save, next run, created).
 * `onDeleteRequest` and `onRunPreview` are optional; when omitted those
 * actions are hidden.
 */
export const AutomationDetailActions: FC<AutomationDetailActionsProps> = (
  props,
) => {
  const {
    automation,
    createdByName,
    isReadOnly,
    onToggleEnabled,
    onDeleteRequest,
    onRunPreview,
    isRunPreviewLoading,
    runPreviewDisabledReason,
  } = props;
  const { is_enabled: isEnabled } = automation;
  const isRunPreviewUnavailable =
    isReadOnly || runPreviewDisabledReason !== undefined;
  const runPreviewButton = onRunPreview ? (
    <Button
      variant={ButtonVariant.Secondary}
      size={Size.SMALL}
      isLoading={isRunPreviewLoading}
      isDisabled={isRunPreviewUnavailable}
      onClick={onRunPreview}
    >
      Run preview
    </Button>
  ) : null;

  return (
    <>
      {onDeleteRequest ? (
        <Button
          variant={ButtonVariant.Warning}
          size={Size.SMALL}
          isDisabled={isReadOnly}
          onClick={onDeleteRequest}
        >
          Delete
        </Button>
      ) : null}
      {onRunPreview ? (
        <TooltipTrigger>
          {isRunPreviewUnavailable ? (
            <TooltipHoverTarget shape="rounded">
              {runPreviewButton}
            </TooltipHoverTarget>
          ) : (
            runPreviewButton
          )}
          <SimpleTooltip>
            {runPreviewDisabledReason ??
              "Runs this automation without sending notifications. The run and its results appear in the Results tab."}
          </SimpleTooltip>
        </TooltipTrigger>
      ) : null}
      <Switch
        isSelected={isEnabled}
        isDisabled={isReadOnly}
        onChange={onToggleEnabled}
        accessibleLabel={isEnabled ? "Disable automation" : "Enable automation"}
      />
      <AutomationInfoPopover
        automation={automation}
        createdByName={createdByName}
      />
    </>
  );
};
